'use client';
import React from 'react';
import { gsap } from 'gsap';
import { getButtonVariant } from '@repo/ui/src/components/Button/utils';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthSmAndUp } from '@repo/ui/src/styles/variables';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { productClicked } from '@repo/ui/src/services/analytics';
import { AmplienceButtonProps, HeaderVariationProps, ImageProps } from '@repo/ui/src/types';

export interface SeriesHeroProps {
  headingLine1: string;
  headingLine2: string;
  headingLine3: string;
  headerVariation: HeaderVariationProps;
  backgroundImage: ImageProps;
  heroButton: AmplienceButtonProps;
  button: AmplienceButtonProps;
  align: 'left' | 'right' | 'center';
  grillImage: ImageProps;
  badgeImage: ImageProps;
  darkBottom?: boolean;
  order?: number;
}

const SeriesHero = ({
  headingLine1,
  headingLine2,
  headingLine3,
  headerVariation,
  backgroundImage,
  heroButton,
  button = heroButton,
  align,
  grillImage,
  badgeImage,
  darkBottom,
  order,
}: SeriesHeroProps) => {
  const { locale } = useCurrentUrl();
  const heroRef = React.useRef<HTMLDivElement>(null);
  const fadeContainerRef = React.useRef<HTMLDivElement>(null);

  const imageUrl = buildImageSrc(backgroundImage, imageFullWidthSmAndUp, 1, 1);

  React.useEffect(() => {
    const hero = heroRef.current;
    const fadeContainer = fadeContainerRef.current;
    if (!hero || !fadeContainer) return;
    const ctx = gsap.context(() => {
      const seriesBannerTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '50% top',
          scrub: 0.5,
        },
      });
      seriesBannerTimeline.to(fadeContainer, {
        opacity: 1,
      });
    });
    // We can't return revert directly or gsap throws an error
    return () => ctx.revert();
  }, []);

  const HeaderEl = headerVariation?.headerVariation ?? 'h2';

  return (
    <section style={{ zIndex: order }}>
      <div className="sm:tw-min-h-[480px] [clip-path:inset(0_0_0_0)]" ref={heroRef}>
        <div className="tw-fixed tw-top-0 tw-w-full tw-h-screen tw-bg-surface-800">
          {backgroundImage?.image && (
            <LazyLoadImage
              imgComponent={
                <img
                  src={imageUrl}
                  alt={backgroundImage.image.name}
                  className="tw-inset-0 tw-absolute tw-object-cover tw-w-full tw-h-full"
                />
              }
            />
          )}
        </div>
        <div
          className="tw-fixed tw-inset-0 tw-bg-black"
          ref={fadeContainerRef}
          style={{ opacity: 0 }}
        />
        <div className="tw-h-full tw-w-full tw-top-0 tw-fixed tw-bg-[length:auto_100%] tw-bg-seriesBackground">
          <div
            className={classNames(
              `tw-fixed tw-inset-0 tw-h-screen tw-grid tw-gap-y-2 tw-place-content-center tw-text-center tw-px-6 tw-pt-48 tw-pb-[40vh] sm:tw-pb-[30vh] sm:tw-pt-56 sm:px-[3vw]`,
              align === 'left' && 'tw-justify-start',
              align === 'right' && 'tw-justify-end',
              align !== 'left' && align !== 'right' && 'tw-justify-center',
            )}
          >
            {headingLine1 && (
              <p
                className={classNames(
                  'tra-text-web-display-2-l tw-text-primary-100 tw-uppercase tw-text-3xl',
                  align === 'left' && 'tw-justify-start',
                  align === 'right' && 'tw-justify-end',
                  align !== 'left' && align !== 'right' && 'tw-justify-center',
                )}
              >
                {headingLine1}
              </p>
            )}
            {headingLine2 && (
              <HeaderEl
                className={classNames(
                  'tra-text-web-display-1-l tw-text-primary-100 tw-m-0 tw-p-0',
                  align === 'left' && 'tw-justify-start',
                  align === 'right' && 'tw-justify-end',
                  align !== 'left' && align !== 'right' && 'tw-justify-center',
                )}
              >
                {headingLine2}
              </HeaderEl>
            )}
            {headingLine3 && (
              <HeaderEl
                className={classNames(
                  'tra-text-web-display-1 tw-text-primary-100 tw-m-0 tw-p-0',
                  align === 'left' && 'tw-justify-start',
                  align === 'right' && 'tw-justify-end',
                  align !== 'left' && align !== 'right' && 'tw-justify-center',
                )}
              >
                {headingLine3}
              </HeaderEl>
            )}
            {button?.link && (
              <Button
                as={Anchor}
                link={button.link.link}
                variant={getButtonVariant(button.style, button.icon)}
                className="tw-mt-8 tw-justify-self-center"
                onClick={() => {
                  productClicked({
                    brand: 'Traeger',
                    locale,
                    affiliation: 'website',
                    category: 'grills',
                    description: '',
                    image_url: imageUrl,
                    list_name: 'Series Hero',
                    name: headingLine2,
                    product_id: '', //  not available here
                    sku: '', //  not available here
                    url: window.location.href,
                  });
                }}
              >
                {button.link.displayName}
              </Button>
            )}
          </div>
        </div>
        <div
          className={classNames(
            'tw-relative tw-pt-[60vh] tw-pointer-events-none sm:landscape:tw-pt-[70vh] after:tw-absolute after:tw-left-0 after:-tw-bottom-px after:tw-w-full after:tw-h-[30%] after:tw-z-[101] after:tw-bg-speckle after:tw-bg-fixed',
            darkBottom ? 'after:tw-bg-surface-800' : 'after:tw-bg-surface-100',
          )}
        >
          <div className="tw-my-0 tw-mx-auto tw-w-full tw-px-6 tw-relative landscape:sm:tw-w-[66vw] md:tw-w-[50vw]">
            <LazyLoadImage
              imgComponent={
                <img
                  src={buildImageSrc(grillImage, 800, 0, 0)}
                  alt={grillImage?.image?.name}
                  className="tw-relative tw-w-full tw-block tw-z-[102]"
                  id="series-grill-image"
                />
              }
            />
            {badgeImage?.image && (
              <LazyLoadImage
                imgComponent={
                  <img
                    src={buildImageSrc(badgeImage, 104, 0, 0)}
                    alt={badgeImage.image.name}
                    id="grill-badge-image"
                  />
                }
                className="tw-p-6 tw-mr-6 tw-absolute tw-top-0 tw-right-0 tw-z-[103]"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeriesHero;
